import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Bank, ShieldCheck, ShieldWarning, CheckCircle, WarningCircle,
  DownloadSimple, FileXls, FileText, Receipt, ArrowsClockwise, ArrowsLeftRight,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { shareOrDownload } from "@/lib/exportXlsx";
import { formatSEK } from "@/lib/utils-app";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const fmt = (n) => formatSEK(n);

export default function LedgerPage() {
  const { user } = useAuth();
  const isMaster = !!user?.is_master;
  const [vouchers, setVouchers] = useState([]);
  const [tb, setTb] = useState(null);
  const [chain, setChain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [busyBackfill, setBusyBackfill] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [v, t, c] = await Promise.all([
        api.get("/ledger/vouchers"),
        api.get("/ledger/trial-balance"),
        api.get("/ledger/chain-verify"),
      ]);
      setVouchers(v.data);
      setTb(t.data);
      setChain(c.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const years = useMemo(() => {
    const set = new Set([new Date().getUTCFullYear()]);
    vouchers.forEach((v) => v.accounting_year && set.add(v.accounting_year));
    return Array.from(set).sort((a, b) => b - a);
  }, [vouchers]);

  // ---- Exports ---------------------------------------------------------
  const downloadVoucherExcel = () => {
    const rows = [];
    rows.push(["Voucher #", "Date", "Description", "Correction?", "Account", "Account name", "Debit (SEK)", "Credit (SEK)", "Hash", "Prev Hash"]);
    vouchers.forEach((v) => {
      v.rows.forEach((r, idx) => {
        rows.push([
          idx === 0 ? v.voucher_number : "",
          idx === 0 ? v.booking_date : "",
          idx === 0 ? v.description : "",
          idx === 0 ? (v.is_correction ? "YES" : "") : "",
          r.account,
          r.account_name,
          r.debit || "",
          r.credit || "",
          idx === 0 ? (v.hash || "").slice(0, 16) + "…" : "",
          idx === 0 ? (v.prev_hash || "").slice(0, 16) + "…" : "",
        ]);
      });
      rows.push([]); // blank row between vouchers
    });
    // Provenance footer
    rows.push([]);
    rows.push(["── PROVENANCE ──"]);
    rows.push(["Total vouchers", vouchers.length]);
    rows.push(["Hash chain valid", chain?.ok ? "YES" : "NO"]);
    rows.push(["Last hash", (chain?.last_hash || "").slice(0, 64)]);
    rows.push(["Exported at (UTC)", new Date().toISOString()]);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 10 }, { wch: 12 }, { wch: 50 }, { wch: 11 }, { wch: 8 }, { wch: 36 }, { wch: 14 }, { wch: 14 }, { wch: 22 }, { wch: 22 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ledger Vouchers");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    shareOrDownload(blob, `TNL_LedgerVouchers_${new Date().toISOString().slice(0, 10)}.xlsx`, "Ledger Vouchers", "BFL-compliant journal export");
  };

  const downloadTrialBalanceExcel = () => {
    if (!tb) return;
    const rows = [["BAS Account", "Account name", "Debit (SEK)", "Credit (SEK)", "Net (SEK)", "Voucher count"]];
    tb.rows.forEach((r) => rows.push([r.account, r.account_name, r.debit, r.credit, r.net, r.voucher_count]));
    rows.push([]);
    rows.push(["── TOTALS ──"]);
    rows.push(["", "Sum Debit", tb.total_debit]);
    rows.push(["", "Sum Credit", tb.total_credit]);
    rows.push(["", "Balanced", tb.balanced ? "YES (Σ debit = Σ credit)" : "NO — INVESTIGATE"]);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 12 }, { wch: 36 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BAS Trial Balance");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    shareOrDownload(blob, `TNL_TrialBalance_${new Date().toISOString().slice(0, 10)}.xlsx`, "BAS Trial Balance", "Per-account totals from immutable ledger");
  };

  const downloadSie4 = async () => {
    try {
      const res = await api.get(`/ledger/sie4?year=${year}`, { responseType: "blob" });
      await shareOrDownload(res.data, `TNL_SIE4_${year}.se`, `SIE-4 ${year}`, "Standard Swedish accounting interchange file");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SIE-4 export failed");
    }
  };

  const downloadBasChartExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1 — Full BAS chart used by TNL
    const chart = [
      ["BAS-kontoplan used by The NordHim Ledger (TNL)"],
      ["Source: BAS 2024 standard — https://www.bas.se"],
      [],
      ["Konto", "Kontonamn (Svenska)", "Category", "Debit / Credit", "Used when"],
      // Revenue
      [3001, "Försäljning varor 25 % moms", "Revenue (3xxx)", "Credit", "Sale to Animal Feed / Other (25 % VAT)"],
      [3002, "Försäljning varor 6 % moms",  "Revenue (3xxx)", "Credit", "Sale to Human Food (6 % VAT)"],
      [3003, "Försäljning varor 12 % moms", "Revenue (3xxx)", "Credit", "(reserved — not currently used)"],
      // Output VAT
      [2611, "Utgående moms 25 %", "Output VAT (26xx)", "Credit", "Alongside 3001 sales"],
      [2620, "Utgående moms 12 %", "Output VAT (26xx)", "Credit", "(reserved — not currently used)"],
      [2630, "Utgående moms 6 %",  "Output VAT (26xx)", "Credit", "Alongside 3002 sales"],
      // Input VAT
      [2641, "Ingående moms 25 %", "Input VAT (264x)", "Debit", "Expense at 25 % VAT (reclaimable)"],
      [2642, "Ingående moms 12 %", "Input VAT (264x)", "Debit", "Expense at 12 % VAT (reclaimable)"],
      [2643, "Ingående moms 6 %",  "Input VAT (264x)", "Debit", "Expense at 6 % VAT (reclaimable)"],
      // Expenses
      [4000, "Inköp varor och material",   "Expense (4xxx)", "Debit", "Category: Grains/Seeds"],
      [5170, "Elektricitet",               "Expense (5xxx)", "Debit", "Category: Electricity"],
      [5190, "Andra fastighetskostnader",  "Expense (5xxx)", "Debit", "Category: Water"],
      [6990, "Övriga externa kostnader",   "Expense (6xxx)", "Debit", "Category: Other"],
      // Assets
      [1220, "Inventarier och verktyg",    "Asset (1xxx)", "Debit",  "Category: Equipment (capitalised, not P&L)"],
      [1510, "Kundfordringar",             "Asset (1xxx)", "Debit",  "(reserved — for future 30-day credit invoices)"],
      [1930, "Företagskonto",              "Asset (1xxx)", "Debit / Credit", "Bank / cash movement — debited on sales, credited on expenses"],
    ];
    const wsChart = XLSX.utils.aoa_to_sheet(chart);
    wsChart["!cols"] = [{ wch: 10 }, { wch: 36 }, { wch: 18 }, { wch: 18 }, { wch: 60 }];
    wsChart["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
    ];
    XLSX.utils.book_append_sheet(wb, wsChart, "BAS Chart");

    // Sheet 2 — Worked examples (double-entry vouchers)
    const examples = [
      ["Worked examples — every voucher balances (Σ debit = Σ credit)"],
      [],
      ["Example 1 — Cash sale 300 SEK to Café Norden (Human Food, 6 % moms)"],
      ["Konto", "Kontonamn", "Debit (SEK)", "Credit (SEK)"],
      [1930, "Företagskonto", 318.00, ""],
      [3002, "Försäljning varor 6 % moms", "", 300.00],
      [2630, "Utgående moms 6 %", "", 18.00],
      ["", "Σ", 318.00, 318.00],
      [],
      ["Example 2 — Sale 1 000 SEK to feed shop (Animal Feed/Other, 25 % moms)"],
      ["Konto", "Kontonamn", "Debit (SEK)", "Credit (SEK)"],
      [1930, "Företagskonto", 1250.00, ""],
      [3001, "Försäljning varor 25 % moms", "", 1000.00],
      [2611, "Utgående moms 25 %", "", 250.00],
      ["", "Σ", 1250.00, 1250.00],
      [],
      ["Example 3 — Grain purchase 1 250 SEK gross (25 % VAT)"],
      ["Konto", "Kontonamn", "Debit (SEK)", "Credit (SEK)"],
      [4000, "Inköp varor och material", 1000.00, ""],
      [2641, "Ingående moms 25 %", 250.00, ""],
      [1930, "Företagskonto", "", 1250.00],
      ["", "Σ", 1250.00, 1250.00],
      [],
      ["Example 4 — Void of Example 1 → Credit Note (Kreditfaktura)"],
      ["Debit / credit are swapped from the original. is_correction=true, corrected_voucher_ref=<original id>"],
      ["Konto", "Kontonamn", "Debit (SEK)", "Credit (SEK)"],
      [3002, "Försäljning varor 6 % moms", 300.00, ""],
      [2630, "Utgående moms 6 %", 18.00, ""],
      [1930, "Företagskonto", "", 318.00],
      ["", "Σ", 318.00, 318.00],
    ];
    const wsEx = XLSX.utils.aoa_to_sheet(examples);
    wsEx["!cols"] = [{ wch: 10 }, { wch: 36 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, wsEx, "Worked Examples");

    // Sheet 3 — Compliance one-pager
    const compliance = [
      ["BFL compliance guarantees (share with your accountant)"],
      [],
      ["#", "Guarantee", "Where it is enforced"],
      [1, "Gap-free voucher numbers per accounting year", "Atomic counter in `bas_counters` collection"],
      [2, "Σ debit = Σ credit validated on every insert", "ledger.post_voucher() rejects unbalanced rows"],
      [3, "SHA-256 hash chain over all vouchers", "prev_hash + hash on each row · /api/ledger/chain-verify"],
      [4, "Append-only after Production Start", "production_started_at flag blocks all delete endpoints (HTTP 423)"],
      [5, "Corrections via new voucher, never edit", "is_correction=true + corrected_voucher_ref (BFL 5 kap. 5 §)"],
      [6, "SIE-4 export (BAS 2024)", "/api/ledger/sie4?year=YYYY"],
      [7, "7-year retention", "retention_required_until = production_started_at + 7 years"],
      [8, "Only EU processors (GDPR)", "MongoDB Atlas EU (Stockholm), Scaleway S3 (Paris)"],
    ];
    const wsC = XLSX.utils.aoa_to_sheet(compliance);
    wsC["!cols"] = [{ wch: 4 }, { wch: 46 }, { wch: 70 }];
    XLSX.utils.book_append_sheet(wb, wsC, "Compliance");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    shareOrDownload(blob, `TNL_BAS_Chart_${new Date().toISOString().slice(0, 10)}.xlsx`, "TNL BAS Chart", "BAS-kontoplan, worked examples, and BFL guarantees");
  };

  const runBackfill = async () => {
    if (!window.confirm("Re-build the immutable ledger from sales / expenses / receipts? This wipes ledger_vouchers and re-inserts in chronological order. Safe to run repeatedly.")) return;
    setBusyBackfill(true);
    try {
      const { data } = await api.post("/admin/ledger/backfill");
      toast.success(`Posted ${data.posted} vouchers · Chain ${data.chain?.ok ? "OK" : "BROKEN"}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBusyBackfill(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header ---------------------------------------------------------- */}
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Tab 5</p>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 flex items-center gap-2">
          <Bank size={32} weight="duotone" className="text-emerald-700" />
          Immutable Ledger
        </h1>
        <p className="text-sm text-slate-500 mt-1">Bokföringslagen-compliant double-entry journal · BAS-kontoplan · SHA-256 hash chain</p>
      </div>

      {/* Status row ------------------------------------------------------- */}
      <div className="grid gap-3 md:grid-cols-3">
        <StatusCard
          title="Hash chain"
          ok={!!chain?.ok}
          okLabel="Intact"
          badLabel={chain?.broken_at_voucher ? `Broken at V#${chain.broken_at_voucher}` : "Broken"}
          icon={<ShieldCheck size={22} weight="duotone" />}
          testId="status-chain"
        />
        <StatusCard
          title="Trial balance (Σdebit = Σcredit)"
          ok={!!tb?.balanced}
          okLabel={tb ? `${fmt(tb.total_debit)} = ${fmt(tb.total_credit)}` : ""}
          badLabel={tb ? `${fmt(tb.total_debit)} vs ${fmt(tb.total_credit)}` : ""}
          icon={<ArrowsLeftRight size={22} weight="duotone" />}
          testId="status-balance"
        />
        <Card className="border-slate-200" data-testid="status-count">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Vouchers</div>
                <div className="font-heading text-2xl font-bold text-slate-900">{vouchers.length}</div>
                <div className="text-xs text-slate-500 mt-0.5">{vouchers.filter((v) => v.is_correction).length} corrections</div>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center"><Receipt size={22} weight="duotone" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export bar ------------------------------------------------------- */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2"><DownloadSimple size={20} weight="duotone" className="text-blue-700" /> Exports</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={downloadVoucherExcel} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="export-vouchers-excel">
              <FileXls size={18} weight="duotone" className="mr-1.5" /> Ledger Vouchers (Excel)
            </Button>
            <Button onClick={downloadTrialBalanceExcel} disabled={!tb} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="export-trial-balance-excel">
              <FileXls size={18} weight="duotone" className="mr-1.5" /> BAS Trial Balance (Excel)
            </Button>
            <Button onClick={downloadBasChartExcel} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="export-bas-chart-excel">
              <FileXls size={18} weight="duotone" className="mr-1.5" /> BAS Chart (Excel)
            </Button>
            <div className="flex items-center gap-2 ml-auto">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">SIE-4 year</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(+v)}>
                <SelectTrigger className="h-9 w-28" data-testid="sie4-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={downloadSie4} className="bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="export-sie4">
                <FileText size={18} weight="duotone" className="mr-1.5" /> SIE-4 File
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 leading-snug">
            <strong>Ledger Vouchers</strong> — full debit/credit journal with hash chain · <strong>Trial Balance</strong> — per-account totals · <strong>SIE-4</strong> — standard Swedish accounting file accepted by Bokio, Visma, Fortnox, and accountants.
          </p>
          {isMaster && (
            <div className="border-t border-slate-200 pt-3 flex items-start gap-3 flex-wrap">
              <div className="text-xs text-slate-600 max-w-md">
                <strong>Master-admin tool:</strong> Re-build the ledger from sales / expenses / receipts. Use after importing legacy data or if the chain is reported broken. Wipes <code>ledger_vouchers</code> first.
              </div>
              <Button onClick={runBackfill} disabled={busyBackfill} variant="outline" className="ml-auto border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="backfill-button">
                <ArrowsClockwise size={16} weight="duotone" className="mr-1.5" /> {busyBackfill ? "Rebuilding…" : "Rebuild Ledger"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trial balance ---------------------------------------------------- */}
      <Card className="border-slate-200" data-testid="trial-balance-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">BAS Trial Balance</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!tb ? <div className="p-6 text-sm text-slate-500">Loading…</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Account</th>
                    <th className="text-left px-4 py-2">Name</th>
                    <th className="text-right px-4 py-2">Debit</th>
                    <th className="text-right px-4 py-2">Credit</th>
                    <th className="text-right px-4 py-2">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {tb.rows.map((r) => (
                    <tr key={r.account} className="border-t border-slate-100" data-testid={`tb-row-${r.account}`}>
                      <td className="px-4 py-2 font-mono text-slate-800">{r.account}</td>
                      <td className="px-4 py-2 text-slate-700">{r.account_name}</td>
                      <td className="px-4 py-2 text-right font-mono">{fmt(r.debit)}</td>
                      <td className="px-4 py-2 text-right font-mono">{fmt(r.credit)}</td>
                      <td className={`px-4 py-2 text-right font-mono font-semibold ${r.net >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmt(r.net)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-300 font-semibold">
                    <td colSpan={2} className="px-4 py-2">TOTALS</td>
                    <td className="px-4 py-2 text-right font-mono">{fmt(tb.total_debit)}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmt(tb.total_credit)}</td>
                    <td className={`px-4 py-2 text-right font-mono ${tb.balanced ? "text-emerald-700" : "text-red-700"}`}>{tb.balanced ? "✓ Balanced" : "⚠ Unbalanced"}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Voucher list ----------------------------------------------------- */}
      <Card className="border-slate-200" data-testid="voucher-list-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">Vouchers ({vouchers.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading…</div>
          ) : vouchers.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">No vouchers yet — click <strong>Rebuild Ledger</strong> to backfill from existing data.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {vouchers.slice().reverse().map((v) => (
                <li key={v.id || v.voucher_number} className="px-4 py-3" data-testid={`voucher-${v.voucher_number}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-bold text-slate-900">V#{String(v.voucher_number).padStart(4, "0")}</span>
                        <span className="text-xs text-slate-500">{v.booking_date}</span>
                        {v.is_correction && (
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 font-bold">CORRECTION</Badge>
                        )}
                        <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]">{v.source_kind}</Badge>
                      </div>
                      <div className="text-sm text-slate-700 mt-1">{v.description}</div>
                      <div className="grid grid-cols-12 gap-2 mt-2 text-xs">
                        {v.rows.map((r, i) => (
                          <React.Fragment key={i}>
                            <div className="col-span-2 font-mono text-slate-500">{r.account}</div>
                            <div className="col-span-5 text-slate-700 truncate">{r.account_name}</div>
                            <div className="col-span-2 text-right font-mono text-blue-700">{r.debit ? fmt(r.debit) : ""}</div>
                            <div className="col-span-3 text-right font-mono text-amber-700">{r.credit ? fmt(r.credit) : ""}</div>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-[10px] text-slate-400 font-mono break-all">
                    hash {(v.hash || "").slice(0, 24)}… · prev {(v.prev_hash || "").slice(0, 24)}…
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const StatusCard = ({ title, ok, okLabel, badLabel, icon, testId }) => (
  <Card className={`border ${ok ? "border-emerald-200 bg-emerald-50/40" : "border-red-200 bg-red-50/40"}`} data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{title}</div>
          <div className={`flex items-center gap-1.5 mt-1 ${ok ? "text-emerald-800" : "text-red-700"}`}>
            {ok ? <CheckCircle size={18} weight="fill" /> : <WarningCircle size={18} weight="fill" />}
            <span className="font-heading font-semibold text-base truncate">{ok ? okLabel : badLabel}</span>
          </div>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{icon}</div>
      </div>
    </CardContent>
  </Card>
);
